"use client";
import React, { useEffect, useRef } from "react";

import Image from "@/components/OptimizedImage";

import { LazyMotion, domAnimation, m, useInView, useAnimation, type Variants } from "motion/react";

import { usePrefersReducedMotion } from "@lightwaves/hooks";

interface ValuePropositionProps {
  text?: string;
}

const ValueProposition = ({ text }: ValuePropositionProps) => {
  const controls = useAnimation();
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.7 });
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (isInView || prefersReducedMotion) {
      controls.start("visible");
    }
  }, [isInView, controls, prefersReducedMotion]);

  // Combined animation variants
  const variants = {
    text: {
      hidden: {
        opacity: 0,
        y: 10,
        filter: "blur(8px)",
      },
      visible: {
        opacity: 1,
        y: 0,
        filter: "blur(0px)",
        transition: {
          duration: 0.9,
          ease: [0.22, 1, 0.36, 1],
        },
      },
    } satisfies Variants,

    image: {
      hidden: {
        opacity: 0,
        scale: 1.05,
        y: -5,
        filter:
          "blur(8px) brightness(1.07) drop-shadow(0 20px 30px rgba(0,0,0,0.15))",
        transition: {
          duration: 0.7,
          ease: [0.25, 0.1, 0.25, 1],
        },
      } ,
      visible: {
        opacity: 1,
        scale: 0.92,
        filter: "blur(0px) brightness(1) drop-shadow(0 0 0 rgba(0,0,0,0))",
        y: 0,
        transition: {
          duration: 0.9,
          ease: [0.22, 1, 0.36, 1],
        },
      },
    } satisfies Variants,

    container: {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.2,
          delayChildren: 0.1,
          ease: "easeOut",
          duration: 0.8,
        },
      },
    } satisfies Variants,
  };

  return (
    <LazyMotion features={domAnimation}>
      <section ref={sectionRef} className="bg-card py-20">
        <div className="container flex flex-col items-center justify-center text-center">
          <m.h2
            className="text-2xl font-bold md:text-xl lg:text-2xl"
            variants={variants.container}
            initial={prefersReducedMotion ? "visible" : "hidden"}
            animate={controls}
          >
            {text && (
              <m.span
                className="m-1.5 inline-block md:m-4"
                variants={variants.text}
              >
                {text}
              </m.span>
            )}
          </m.h2>
        </div>
      </section>
    </LazyMotion>
  );
};

export default ValueProposition;
